import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send, Loader2, Search, Tag, User, Sparkles, ChevronRight,
  Eye, Users2, Clock, Paperclip, X, Trash2, RefreshCw, ImageIcon,
} from "lucide-react";
import { cropToHeader } from "../lib/cropHeaderImage";

/**
 * Executive Stationery · Custom Send Tool.
 *
 * Features:
 *  • Single or Cohort recipient picker (checkbox multi-select + cohort presets)
 *  • Live obsidian/gold HTML preview (iframe srcDoc)
 *  • Schedule sends via datetime picker (backend APScheduler dispatches)
 *  • Attachments (PDF etc.) via base64 upload
 *  • Smart Tag chip inserter (resolves per-recipient)
 */

const TEMPLATES = [
  { id: "invitation", title: "Invitation · 5151 Series", accent: "#D8B23E",
    blurb: "Formal cohort invite for a nominated buyer.",
    defaultSubject: "You are invited to the Vinci 5151 Series",
    fields: [
      { key: "series_slot", label: "Cohort slot", placeholder: "Series I · Cohort 5151", type: "text" },
      { key: "invite_link", label: "Accept link", placeholder: "https://realtymaze.com/join?code=…", type: "text" },
      { key: "personal_note", label: "Personal note (optional)", placeholder: "The desk noticed your record turnover.", type: "textarea" },
      { key: "vinci_code", label: "Vinci Code (optional)", placeholder: "auto: user's vendor code", type: "text" },
    ] },
  { id: "agreement_nudge", title: "Agreement Nudge", accent: "#E5484D",
    blurb: "Firm reminder for a pending e-signature.",
    defaultSubject: "Signature required · Vinci Partner Agreement",
    fields: [
      { key: "agreement_name", label: "Agreement name", placeholder: "Vinci Partner Agreement", type: "text" },
      { key: "sign_link", label: "Zoho Sign link", placeholder: "https://sign.zoho.in/…", type: "text" },
      { key: "deadline_pretty", label: "Deadline", placeholder: "the next 48 hours", type: "text" },
      { key: "custom_note", label: "Custom note (optional)", placeholder: "Ops has held your credit line open.", type: "textarea" },
    ] },
  { id: "market_alert", title: "Market Alert · Rates Bulletin", accent: "#2FE4C6",
    blurb: "Manual dispatch of the monthly rate bulletin to VIP clients.",
    defaultSubject: "Vinci Rates Bulletin · [Month_Label]",
    fields: [
      { key: "month_label", label: "Month label", placeholder: "February 2026", type: "text" },
      { key: "steel_rate", label: "Steel / TMT rate", placeholder: "₹58,400 / MT", type: "text" },
      { key: "cement_rate", label: "Cement rate", placeholder: "₹412 / bag", type: "text" },
      { key: "bulletin_link", label: "Bulletin link", placeholder: "https://realtymaze.com/rates-bulletin", type: "text" },
      { key: "note", label: "VIP note (optional)", placeholder: "Cohort-locked. Allocate early.", type: "textarea" },
    ] },
  { id: "settlement_notice", title: "Settlement Notice", accent: "#2FE4C6",
    blurb: "Professional statement for a supplier after a payout.",
    defaultSubject: "Vinci Settlement Notice · [settlement_ref]",
    fields: [
      { key: "settlement_ref", label: "Settlement ref", placeholder: "SETL-2026-0001", type: "text" },
      { key: "amount_inr", label: "Amount (INR)", placeholder: "1250000", type: "number" },
      { key: "period_label", label: "Period", placeholder: "Feb 2026 · Cycle 01", type: "text" },
      { key: "utr_or_txn", label: "UTR / Txn (optional)", placeholder: "HDFC26020112345678", type: "text" },
      { key: "statement_link", label: "Statement link (optional)", placeholder: "https://realtymaze.com/account/settlements", type: "text" },
    ] },
  { id: "event_invite", title: "Event Invite · Jitsi", accent: "#2FE4C6",
    blurb: "Webinar or Virtual Trade Desk session invitation.",
    defaultSubject: "Invitation · [event_title]",
    fields: [
      { key: "event_title", label: "Event title", placeholder: "Vinci Virtual Trade Desk · Weekly Briefing", type: "text" },
      { key: "event_date", label: "Date", placeholder: "Fri, 21 Feb 2026", type: "text" },
      { key: "event_time_ist", label: "Time (IST)", placeholder: "16:30 IST", type: "text" },
      { key: "join_link", label: "Jitsi join link", placeholder: "https://meet.jit.si/vinci-desk-…", type: "text" },
      { key: "agenda", label: "Agenda (optional)", placeholder: "Rates outlook · Credit review · Open Q&A", type: "textarea" },
      { key: "host_name", label: "Host name (optional)", placeholder: "The Vinci Trade Desk", type: "text" },
    ] },
  { id: "festival", title: "Festival Wishing · India", accent: "#D8B23E",
    blurb: "Festival-centric stationery — unique hero art per festival.",
    defaultSubject: "Warm wishes from Vinci Inc.",
    fields: [
      { key: "festival", label: "Festival", type: "select", options: [
          ["diwali","Diwali · Deepavali"],["holi","Holi · होली"],["eid","Eid al-Fitr · عيد الفطر"],
          ["eid_ul_zuha","Eid al-Adha · عيد الأضحى"],["christmas","Christmas"],["new_year","New Year · MMXXVI"],
          ["raksha","Raksha Bandhan"],["independence","Independence Day"],["republic","Republic Day"],
          ["ganesh","Ganesh Chaturthi"],["navratri","Navratri"],
          ["navratri_durga","Happy Navratri · Maa Durga letter (Vinci Inc. wishes)"],
          ["navratri_card","Happy Navratri · Vinci greeting card (full artwork)"],["dussehra","Dussehra"],
          ["durga_puja","Durga Puja"],["karva","Karva Chauth"],["makar","Makar Sankranti"],
          ["pongal","Pongal"],["baisakhi","Baisakhi"],["onam","Onam"],
          ["janmashtami","Janmashtami · Eye Aura"],
          ["janmashtami_full","Janmashtami · Full Feather"],
          ["janmashtami_trio","Janmashtami · Krishna's Crown (3 feathers)"],
          ["janmashtami_makhan","Janmashtami · Makhan Chor (feather + butter pot)"],
          ["guru_nanak","Guru Nanak Jayanti"],
          ["gandhi","Gandhi Jayanti"],["lohri","Lohri"],["gudi_padwa","Gudi Padwa"],
          ["ugadi","Ugadi"],["vishu","Vishu"],
        ] },
      { key: "custom_message", label: "Custom message (optional)", placeholder: "Overrides the default festival copy", type: "textarea" },
    ] },
];

const COHORT_PRESETS = [
  { key: "all_verified",         label: "All KYC-verified partners" },
  { key: "kyc_verified_sellers", label: "KYC-verified stockists / sellers only" },
  { key: "active",               label: "All active partners" },
  { key: "all_users",            label: "Every non-admin user" },
];

export function AdminCustomSendTool({ api }) {
  // ---- Mode + template ------------------------------------------------------
  const [mode, setMode] = useState("single"); // single | cohort
  const [templateId, setTemplateId] = useState(TEMPLATES[0].id);
  const template = useMemo(() => TEMPLATES.find(t => t.id === templateId) || TEMPLATES[0], [templateId]);
  const [subject, setSubject] = useState(TEMPLATES[0].defaultSubject);
  const [subjectTouched, setSubjectTouched] = useState(false);
  const [params, setParams] = useState({});

  // ---- Recipient picking ----------------------------------------------------
  const [recipients, setRecipients] = useState([]);
  const [loadingRecip, setLoadingRecip] = useState(false);
  const [query, setQuery] = useState("");
  const [singleUser, setSingleUser] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [cohortPreset, setCohortPreset] = useState(""); // "" | preset key
  const [showRecipDropdown, setShowRecipDropdown] = useState(false);

  // ---- Preview / Schedule / Attachments / Schedules list --------------------
  const [smartTags, setSmartTags] = useState([]);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [attachments, setAttachments] = useState([]); // {filename, content_type, content, sizeKb}
  const [scheduleAt, setScheduleAt] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [sending, setSending] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [headerImage, setHeaderImage] = useState(null); // {id, url, preview, alt} — persists across template switches until removed
  const [headerUploading, setHeaderUploading] = useState(false);
  const [headerLibrary, setHeaderLibrary] = useState([]);
  const headerInputRef = useRef(null);
  const sendParams = useMemo(() => (headerImage ? { ...params, header_image_url: headerImage.url, header_image_alt: headerImage.alt || "" } : params), [params, headerImage]);
  const loadHeaderLibrary = useCallback(() => api.get("/admin/communications/header-images?limit=5").then(r => setHeaderLibrary(r.data.items || [])).catch(() => {}), [api]);
  useEffect(() => { loadHeaderLibrary(); }, [loadHeaderLibrary]);

  // ---- Load once ------------------------------------------------------------
  const loadRecipients = useCallback(async (q = "") => {
    setLoadingRecip(true);
    try {
      const r = await api.get(`/admin/communications/recipients`, { params: { q, limit: 100 } });
      setRecipients(r.data.recipients || []);
    } catch { toast.error("Could not load partners"); }
    finally { setLoadingRecip(false); }
  }, [api]);

  const loadSchedules = useCallback(async () => {
    setLoadingSchedules(true);
    try {
      const r = await api.get(`/admin/communications/schedules`, { params: { status: "pending" } });
      setSchedules(r.data.schedules || []);
    } catch { /* no-op */ }
    finally { setLoadingSchedules(false); }
  }, [api]);

  useEffect(() => {
    loadRecipients("");
    api.get("/admin/communications/smart-tags").then(r => setSmartTags(r.data.tags || [])).catch(() => {});
    loadSchedules();
  }, [api, loadRecipients, loadSchedules]);

  useEffect(() => {
    const t = setTimeout(() => loadRecipients(query), 250);
    return () => clearTimeout(t);
  }, [query, loadRecipients]);

  useEffect(() => {
    setParams({});
    if (!subjectTouched) setSubject(template.defaultSubject);
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const FESTIVAL_SUBJECTS = { navratri_durga: "Warm Navratri Wishes from Vinci Inc.", navratri_card: "Warm Navratri Wishes from Vinci Inc." };
  useEffect(() => {
    if (templateId === "festival" && !subjectTouched) setSubject(FESTIVAL_SUBJECTS[params.festival] || template.defaultSubject);
  }, [params.festival]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Helpers --------------------------------------------------------------
  const insertSmartTag = (tag) => { setSubject(p => (p || "") + tag); setSubjectTouched(true); };
  const updateParam = (key, val) => setParams(p => ({ ...p, [key]: val }));

  const toggleId = (id) => setSelectedIds(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const cohortSize = useMemo(() => {
    if (mode === "single") return singleUser ? 1 : 0;
    if (cohortPreset) return null; // server-resolved
    return selectedIds.size;
  }, [mode, singleUser, cohortPreset, selectedIds]);

  // ---- Attachment handling --------------------------------------------------
  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (f.size > 4 * 1024 * 1024) { toast.error(`${f.name} > 4MB — pick a smaller file`); continue; }
      const b64 = await new Promise((res, rej) => {
        const rdr = new FileReader();
        rdr.onload = () => res((rdr.result || "").toString().split(",")[1] || "");
        rdr.onerror = rej;
        rdr.readAsDataURL(f);
      });
      setAttachments(prev => [...prev, {
        filename: f.name, content_type: f.type || "application/octet-stream",
        content: b64, sizeKb: Math.round(f.size / 102.4) / 10,
      }]);
    }
    e.target.value = "";
  };
  const removeAttachment = (i) => setAttachments(prev => prev.filter((_, idx) => idx !== i));

  // ---- Header image (crop → upload → preview) --------------------------------
  const onHeaderImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setHeaderUploading(true);
    try {
      const { dataUrl, contentType, base64 } = await cropToHeader(f);
      const r = await api.post("/admin/communications/header-image", { content_type: contentType, content: base64 });
      setHeaderImage({ id: r.data.id, url: r.data.url, preview: dataUrl, alt: "" });
      loadHeaderLibrary();
      toast.success("Header image applied");
      if (previewOpen) runPreview({ ...params, header_image_url: r.data.url });
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Could not upload header image"); }
    finally { setHeaderUploading(false); }
  };
  const applyLibraryHeader = (item) => {
    setHeaderImage({ id: item.id, url: item.url, preview: item.url, alt: item.alt || "" });
    toast.success("Header preset applied");
    if (previewOpen) runPreview({ ...params, header_image_url: item.url, header_image_alt: item.alt || "" });
  };
  const saveHeaderAlt = async () => {
    if (!headerImage?.id) return;
    try { await api.patch(`/admin/communications/header-images/${headerImage.id}`, { alt: headerImage.alt || "" }); loadHeaderLibrary(); if (previewOpen) runPreview(sendParams); }
    catch { /* alt still travels with the send params */ }
  };
  const removeHeaderImage = () => {
    setHeaderImage(null);
    toast.info("Reverted to the default template header");
    if (previewOpen) runPreview(params);
  };

  // ---- Preview --------------------------------------------------------------
  const runPreview = async (overrideParams) => {
    setPreviewLoading(true);
    try {
      const body = {
        template: templateId, subject: subject || undefined,
        params: overrideParams && !overrideParams.nativeEvent ? overrideParams : sendParams,
        ...(singleUser ? { user_id: singleUser.id } : {}),
      };
      const r = await api.post("/admin/communications/preview", body);
      setPreviewHtml(r.data.html || "");
      setPreviewSubject(r.data.subject || "");
      setPreviewOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setPreviewLoading(false); }
  };

  // ---- Send (single or cohort) ---------------------------------------------
  const send = async () => {
    setSending(true);
    try {
      if (mode === "single") {
        if (!singleUser) { toast.error("Pick a recipient first"); return; }
        const r = await api.post("/admin/communications/send-to-user", {
          user_id: singleUser.id, template: templateId,
          subject: subject || undefined, params: sendParams, attachments,
        });
        toast.success(`Sent to ${r.data.sent_to}`);
      } else {
        if (!cohortPreset && selectedIds.size === 0) { toast.error("Pick recipients or a cohort preset"); return; }
        const body = {
          template: templateId, subject: subject || undefined, params: sendParams, attachments,
          ...(cohortPreset ? { target: cohortPreset } : { user_ids: Array.from(selectedIds) }),
        };
        const r = await api.post("/admin/communications/send-to-cohort", body);
        toast.success(`Cohort dispatched · ${r.data.sent} sent, ${r.data.failed} failed`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  // ---- Schedule -------------------------------------------------------------
  const schedule = async () => {
    if (!scheduleAt) { toast.error("Pick a date + time"); return; }
    if (mode === "single" && !singleUser) { toast.error("Pick a recipient first"); return; }
    if (mode === "cohort" && !cohortPreset && selectedIds.size === 0) {
      toast.error("Pick recipients or a cohort preset"); return;
    }
    setScheduling(true);
    try {
      const iso = new Date(scheduleAt).toISOString();
      const body = {
        template: templateId, subject: subject || undefined, params: sendParams, attachments,
        send_at: iso,
        ...(mode === "single"
              ? { user_ids: [singleUser.id] }
              : (cohortPreset ? { target: cohortPreset } : { user_ids: Array.from(selectedIds) })),
      };
      const r = await api.post("/admin/communications/schedule", body);
      toast.success(`Scheduled · fires at ${new Date(r.data.send_at).toLocaleString()}`);
      setScheduleAt("");
      loadSchedules();
    } catch (e) { toast.error(e.response?.data?.detail || "Schedule failed"); }
    finally { setScheduling(false); }
  };

  const cancelSchedule = async (id) => {
    if (!window.confirm("Cancel this scheduled send?")) return;
    try {
      await api.delete(`/admin/communications/schedules/${id}`);
      toast.success("Scheduled send cancelled");
      loadSchedules();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  // -----------------------------------------------------------------------
  return (
    <div data-testid="admin-custom-send" className="border border-white/10 rounded-sm bg-[#0a0a0a] p-6 space-y-5">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] flex items-center justify-center">
          <Sparkles size={18}/>
        </div>
        <div className="flex-1">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E]">
            Executive Stationery · Custom Send
          </div>
          <h4 className="font-serif text-xl text-[#F5F5F0] mt-1">Compose, preview, schedule.</h4>
          <p className="font-body text-xs text-[#8A8A82] mt-1">
            Fire one letter or a whole cohort · Smart Tags resolve per-recipient · Attach PDFs · Queue for later.
          </p>
        </div>
        {/* Mode switch */}
        <div className="inline-flex rounded-sm border border-white/15 overflow-hidden shrink-0">
          {[["single","Single",User],["cohort","Cohort",Users2]].map(([k,label,Icon]) => (
            <button key={k} data-testid={`custom-send-mode-${k}`}
                    onClick={() => setMode(k)}
                    className={`font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-2 inline-flex items-center gap-1.5 ${
                      mode === k ? "bg-[#D8B23E] text-black" : "text-[#8A8A82] hover:text-[#F5F5F0]"
                    }`}>
              <Icon size={11}/> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Recipient picker */}
      <div>
        <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-2">
          {mode === "single" ? "Recipient" : "Recipients / Cohort"}
        </label>

        {mode === "cohort" && (
          <div className="mb-3 grid sm:grid-cols-4 gap-2">
            {COHORT_PRESETS.map(p => (
              <button key={p.key} data-testid={`custom-send-preset-${p.key}`}
                onClick={() => { setCohortPreset(cohortPreset === p.key ? "" : p.key); setSelectedIds(new Set()); }}
                className={`text-left border rounded-sm px-3 py-2 font-body text-[11px] leading-snug ${
                  cohortPreset === p.key ? "bg-[#D8B23E]/15 border-[#D8B23E] text-[#F5F5F0]"
                                          : "bg-black/30 border-white/10 text-[#8A8A82] hover:border-white/25"
                }`}>
                {p.label}
              </button>
            ))}
          </div>
        )}

        {(mode === "single" || !cohortPreset) && (
        <div className="relative">
          <div className="flex items-center gap-2 bg-black/40 border border-white/15 rounded-sm px-3 py-2 focus-within:border-[#D8B23E]">
            <Search size={14} className="text-[#8A8A82]"/>
            <input
              data-testid="custom-send-recipient-search"
              type="text"
              value={mode === "single" && singleUser ? `${singleUser.name} · ${singleUser.email}` : query}
              onFocus={() => { setShowRecipDropdown(true); if (mode === "single" && singleUser) { setSingleUser(null); setQuery(""); } }}
              onChange={(e) => { setQuery(e.target.value); if (mode === "single") setSingleUser(null); setShowRecipDropdown(true); }}
              placeholder={mode === "single" ? "Search by name, email or vendor code…" : "Search & tick to add partners…"}
              className="flex-1 bg-transparent text-sm text-[#F5F5F0] focus:outline-none"
            />
            {loadingRecip && <Loader2 size={12} className="animate-spin text-[#8A8A82]"/>}
          </div>
          <AnimatePresence>
            {showRecipDropdown && recipients.length > 0 && (
              <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-sm shadow-2xl">
                {recipients.map((r) => {
                  const active = mode === "single" ? (singleUser?.id === r.id) : selectedIds.has(r.id);
                  return (
                    <button key={r.id}
                      data-testid={`custom-send-recipient-option-${r.email}`}
                      onClick={() => {
                        if (mode === "single") { setSingleUser(r); setShowRecipDropdown(false); setQuery(""); }
                        else { toggleId(r.id); }
                      }}
                      className={`w-full text-left px-3 py-2 border-b border-white/5 flex items-center gap-3 ${active ? "bg-[#D8B23E]/10" : "hover:bg-white/5"}`}>
                      <div className="h-8 w-8 rounded-full bg-[#D8B23E]/10 text-[#D8B23E] flex items-center justify-center shrink-0">
                        {mode === "cohort" ? (active ? "✓" : "○") : <User size={12}/>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-body text-sm text-[#F5F5F0] truncate">{r.name}</div>
                        <div className="font-body text-[11px] text-[#8A8A82] truncate">
                          {r.email}{r.vendor_code ? ` · ${r.vendor_code}` : ""}{r.kyc_status ? ` · ${r.kyc_status}` : ""}
                        </div>
                      </div>
                      {mode === "single" && <ChevronRight size={12} className="text-[#8A8A82] shrink-0"/>}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        )}

        <div className="mt-2 font-body text-[11px] text-[#8A8A82]">
          {mode === "single" && singleUser && (
            <>Selected · <span className="text-[#D8B23E]">{singleUser.name}</span> · <span className="font-mono">{singleUser.email}</span></>
          )}
          {mode === "cohort" && (cohortPreset
            ? <>Cohort preset · <span className="text-[#D8B23E]">{COHORT_PRESETS.find(p=>p.key===cohortPreset)?.label}</span> · size resolves at dispatch</>
            : (selectedIds.size > 0 ? <>Selected · <span className="text-[#D8B23E]">{selectedIds.size} partners</span></> : "Tick partners above or pick a preset."))}
        </div>
      </div>

      {/* Template picker */}
      <div>
        <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-2">Template</label>
        <div className="grid sm:grid-cols-2 gap-2">
          {TEMPLATES.map((t) => (
            <button key={t.id} data-testid={`custom-send-template-${t.id}`}
              onClick={() => { setTemplateId(t.id); setSubjectTouched(false); }}
              className={`text-left border rounded-sm p-3 transition-colors ${
                templateId === t.id ? "bg-white/5 border-[color:var(--acc)]" : "bg-black/30 border-white/10 hover:border-white/25"
              }`} style={{ "--acc": t.accent }}>
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Template</div>
              <div className="font-serif text-sm text-[#F5F5F0] mt-1">{t.title}</div>
              <div className="font-body text-[11px] text-[#8A8A82] mt-1 leading-relaxed">{t.blurb}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Subject + Smart Tag chips */}
      <div>
        <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-2">Subject line</label>
        <input data-testid="custom-send-subject" type="text" value={subject}
          onChange={(e) => { setSubject(e.target.value); setSubjectTouched(true); }}
          placeholder={template.defaultSubject}
          className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
        {smartTags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#6f6f68] inline-flex items-center gap-1 pr-1">
              <Tag size={9}/> Smart Tags
            </span>
            {smartTags.map((tag) => (
              <button key={tag} onClick={() => insertSmartTag(tag)}
                data-testid={`custom-send-tag-${tag.replace(/[[\]]/g, "")}`}
                className="font-mono text-[10px] px-2 py-0.5 rounded-sm border border-[#2FE4C6]/30 text-[#2FE4C6] hover:bg-[#2FE4C6]/10">
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Template params */}
      {template.fields.length > 0 && (
        <div>
          <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-2">
            {template.title} · Details
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            {template.fields.map((f) => (
              <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                <div className="font-body text-[11px] text-[#8A8A82] mb-1">{f.label}</div>
                {f.type === "textarea" ? (
                  <textarea data-testid={`custom-send-param-${f.key}`} rows={3}
                    value={params[f.key] || ""} onChange={(e) => updateParam(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
                ) : f.type === "select" ? (
                  <select data-testid={`custom-send-param-${f.key}`}
                    value={params[f.key] || (f.options[0]?.[0] || "")}
                    onChange={(e) => updateParam(f.key, e.target.value)}
                    className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]">
                    {f.options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                  </select>
                ) : (
                  <input data-testid={`custom-send-param-${f.key}`} type={f.type}
                    value={params[f.key] || ""} onChange={(e) => updateParam(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attachments */}
      <div>
        <label className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-2">
          Attachments (PDF, images · max 4 MB each)
        </label>
        <div className="flex flex-wrap gap-2 items-center">
          <label data-testid="custom-send-attach-btn"
                 className="cursor-pointer inline-flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.24em] px-3 py-2 rounded-sm border border-white/15 text-[#F5F5F0] hover:bg-white/5">
            <Paperclip size={12}/> Attach file
            <input type="file" multiple onChange={onAttach} className="hidden"/>
          </label>
          {attachments.map((a, i) => (
            <div key={i} data-testid={`custom-send-attachment-${i}`}
                 className="inline-flex items-center gap-2 bg-black/40 border border-white/15 rounded-sm px-3 py-1.5 text-xs text-[#F5F5F0]">
              <span className="truncate max-w-[180px]">{a.filename}</span>
              <span className="text-[#8A8A82]">{a.sizeKb} KB</span>
              <button onClick={() => removeAttachment(i)} className="text-[#E5484D] hover:text-[#ff6b70]"><X size={12}/></button>
            </div>
          ))}
        </div>
      </div>

      {/* Preview + Schedule + Send row */}
      <div className="pt-2 border-t border-white/10 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="custom-send-preview" onClick={runPreview} disabled={previewLoading}
            className="border border-[#2FE4C6]/40 text-[#2FE4C6] hover:bg-[#2FE4C6]/10 font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2.5 rounded-sm inline-flex items-center gap-2">
            {previewLoading ? <Loader2 size={12} className="animate-spin"/> : <Eye size={12}/>}
            Preview
          </button>
          <button data-testid="custom-send-submit" onClick={send} disabled={sending}
            className="bg-[#D8B23E] hover:bg-[#c5a233] disabled:opacity-40 text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-2.5 rounded-sm inline-flex items-center gap-2">
            {sending ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>}
            Send now
          </button>
          <div className="w-full flex flex-wrap items-center gap-2">
            <Clock size={12} className="text-[#8A8A82]"/>
            <input data-testid="custom-send-schedule-at" type="datetime-local" value={scheduleAt}
              onChange={e => setScheduleAt(e.target.value)}
              className="bg-black/40 border border-white/15 rounded-sm px-2 py-2 text-xs text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
            <input ref={headerInputRef} type="file" accept="image/jpeg,image/png,image/gif" className="hidden"
              data-testid="custom-send-header-image-input" onChange={onHeaderImage}/>
            <button data-testid="custom-send-header-image-btn" onClick={() => headerInputRef.current?.click()} disabled={headerUploading}
              className={`border font-grotesk text-[10px] uppercase tracking-[0.24em] px-3 py-2 rounded-sm inline-flex items-center gap-2 whitespace-nowrap disabled:opacity-40 ${
                headerImage ? "border-[#2FE4C6]/60 text-[#2FE4C6] hover:bg-[#2FE4C6]/10" : "border-[#D8B23E]/50 text-[#D8B23E] hover:bg-[#D8B23E]/10"}`}>
              {headerUploading ? <Loader2 size={12} className="animate-spin"/> : <ImageIcon size={12}/>}
              Upload header image
            </button>
            <button data-testid="custom-send-schedule-btn" onClick={schedule} disabled={scheduling || !scheduleAt}
              className="border border-[#D8B23E]/50 text-[#D8B23E] hover:bg-[#D8B23E]/10 disabled:opacity-40 font-grotesk text-[10px] uppercase tracking-[0.24em] px-3 py-2 rounded-sm inline-flex items-center gap-2">
              {scheduling ? <Loader2 size={12} className="animate-spin"/> : <Clock size={12}/>}
              Schedule
            </button>
          </div>
        </div>
        <div className="font-body text-[11px] text-[#8A8A82]">
          Every send bypasses the master switch and is logged in Admin Notifications. Scheduled sends fire once, on time, even if the admin is offline.
        </div>
        {headerImage && (
          <div data-testid="custom-send-header-image-preview" className="flex flex-wrap items-center gap-3 border border-[#2FE4C6]/30 bg-[#2FE4C6]/[0.05] rounded-sm p-2 pr-3">
            <img src={headerImage.preview} alt={headerImage.alt || "Header"} className="h-12 w-[110px] object-cover rounded-[2px] border border-white/10"/>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#2FE4C6] inline-flex items-center gap-2">
              <ImageIcon size={11}/> Custom header · 600×260
            </div>
            <input data-testid="custom-send-header-image-alt" value={headerImage.alt || ""} maxLength={160}
              onChange={e => setHeaderImage(h => ({ ...h, alt: e.target.value }))} onBlur={saveHeaderAlt}
              placeholder="Alt text / caption for image-blocked inboxes"
              className="flex-1 min-w-[220px] bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 text-xs text-[#F5F5F0] focus:outline-none focus:border-[#2FE4C6]"/>
            <button data-testid="custom-send-header-image-remove" onClick={removeHeaderImage} title="Remove header image"
              className="h-6 w-6 grid place-items-center rounded-sm border border-white/15 text-[#8A8A82] hover:text-[#E5484D] hover:border-[#E5484D]/50">
              <X size={12}/>
            </button>
          </div>
        )}
        {headerLibrary.length > 0 && (
          <div data-testid="custom-send-header-library" className="flex flex-wrap items-center gap-2">
            <span className="font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82] mr-1">Recent headers</span>
            {headerLibrary.map(item => (
              <button key={item.id} type="button" data-testid={`custom-send-header-preset-${item.id}`} onClick={() => applyLibraryHeader(item)}
                title={item.alt || new Date(item.created_at).toLocaleDateString()}
                className={`h-10 w-[92px] rounded-[2px] overflow-hidden border transition-colors ${headerImage?.id === item.id ? "border-[#2FE4C6]" : "border-white/10 hover:border-[#D8B23E]/60"}`}>
                <img src={item.url} alt={item.alt || "Header preset"} className="h-full w-full object-cover"/>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Preview iframe */}
      <AnimatePresence>
        {previewOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="border border-[#2FE4C6]/30 rounded-sm bg-black/40 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#2FE4C6]/20 bg-[#2FE4C6]/[0.06]">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#2FE4C6] inline-flex items-center gap-2">
                <Eye size={11}/> Preview · <span className="text-[#F5F5F0] normal-case tracking-normal font-body">{previewSubject}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={runPreview} className="text-[#8A8A82] hover:text-[#F5F5F0]"><RefreshCw size={12}/></button>
                <button data-testid="custom-send-preview-close" onClick={() => setPreviewOpen(false)} className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={12}/></button>
              </div>
            </div>
            <iframe data-testid="custom-send-preview-iframe" title="stationery-preview"
                    srcDoc={previewHtml} className="w-full" style={{ height: 640, border: 0, background: "#050506" }}/>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scheduled sends inbox */}
      <div className="border-t border-white/10 pt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82] inline-flex items-center gap-2">
            <Clock size={11}/> Pending scheduled sends · <span className="text-[#D8B23E]">{schedules.length}</span>
          </div>
          <button onClick={loadSchedules} className="text-[#8A8A82] hover:text-[#F5F5F0]" data-testid="custom-send-schedules-refresh">
            {loadingSchedules ? <Loader2 size={11} className="animate-spin"/> : <RefreshCw size={11}/>}
          </button>
        </div>
        {schedules.length === 0 ? (
          <div className="font-body text-[11px] text-[#6f6f68] italic">No pending scheduled sends.</div>
        ) : (
          <div className="grid gap-2">
            {schedules.map(s => (
              <div key={s.id} data-testid={`custom-send-schedule-row-${s.id}`}
                   className="flex items-center gap-3 border border-white/10 rounded-sm bg-black/30 px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="font-body text-sm text-[#F5F5F0]">
                    <span className="text-[#D8B23E]">{s.template}</span>
                    {s.subject && <span className="text-[#8A8A82]"> · {s.subject}</span>}
                  </div>
                  <div className="font-body text-[11px] text-[#8A8A82]">
                    Fires at {new Date(s.send_at).toLocaleString("en-IN")} ·
                    {" "}Target: {s.target || `${(s.user_ids||[]).length} partners`} ·
                    {" "}Set by {s.created_by_email || "admin"}
                  </div>
                </div>
                <button onClick={() => cancelSchedule(s.id)}
                        data-testid={`custom-send-schedule-cancel-${s.id}`}
                        className="text-[#E5484D] hover:text-[#ff6b70] inline-flex items-center gap-1 font-grotesk text-[10px] uppercase tracking-[0.22em]">
                  <Trash2 size={11}/> Cancel
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
